import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey } from '../../api/api-error';
import { ensureOk } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { AuthError, AuthLayout, AuthLinks } from '../../components/auth/auth-dialog';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';
import { takeFragmentToken } from '../../lib/fragment-token';
import { newPasswordSchema } from './password-schema';

const schema = z
  .object({ password: newPasswordSchema, confirm: z.string() })
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'validation.passwordsDiffer',
  });
type Values = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const { t } = useI18n();
  const [token] = useState(takeFragmentToken);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });
  const reset = useMutation({
    mutationFn: async (values: Values) => {
      ensureOk(
        await api.POST('/api/v1/auth/password/reset', {
          body: { token: token ?? '', password: values.password },
        }),
      );
    },
  });

  if (token === null) {
    return (
      <AuthLayout title={t('auth.reset.title')}>
        <AuthError>{t('error.AUTH_TOKEN_INVALID')}</AuthError>
        <AuthLinks>
          <Link to="/forgot-password">{t('auth.login.forgot')}</Link>
        </AuthLinks>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.reset.title')}>
      {reset.isSuccess ? (
        <>
          <Notice tone="success">{t('auth.reset.done')}</Notice>
          <AuthLinks>
            <Link to="/login">{t('auth.toLogin')}</Link>
          </AuthLinks>
        </>
      ) : (
        <form
          className="space-y-4"
          onSubmit={form.handleSubmit((values) => reset.mutate(values))}
          noValidate
        >
          <TextField
            control={form.control}
            name="password"
            type="password"
            label={t('auth.field.newPassword')}
            autoComplete="new-password"
            description={t('auth.field.passwordHint')}
            autoFocus
          />
          <TextField
            control={form.control}
            name="confirm"
            type="password"
            label={t('auth.field.confirmPassword')}
            autoComplete="new-password"
          />
          {reset.isError && <AuthError>{t(errorMessageKey(reset.error))}</AuthError>}
          <Button type="submit" className="w-full" disabled={reset.isPending}>
            {t('auth.reset.submit')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
